/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useState } from "react";
import { useApp } from "../../../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { toast } from "../../../components/ui/sonner";
import { Loader2 } from "lucide-react";
import { inp } from "../hrmsShared";
import { L, fmtY, errMsg } from "./cnShared";

const TEXT_FIELDS = [["siCity", "参保地", "SI city"], ["siNo", "社保编号", "SI number"], ["hfNo", "公积金账号", "HF account"], ["bankName", "开户行", "Bank"], ["bankAccount", "银行账号", "Bank account"]];

export const CnProfileDialog = ({ row, cfg, zh, onClose, onSaved }) => {
  const { api } = useApp();
  const T = (z, e) => (zh ? z : e);
  const p = row.cnPay || {};
  const [f, setF] = useState({
    ...Object.fromEntries(cfg.wageItems.map((it) => [it[0], p[it[0]] ?? ""])),
    siBase: p.siBase || "", hfBase: p.hfBase || "", hfRateEr: p.hfRateEr || "", hfRateEe: p.hfRateEe || "",
    siEnrolled: p.siEnrolled !== false, hfEnrolled: p.hfEnrolled !== false,
    ...Object.fromEntries(TEXT_FIELDS.map((it) => [it[0], p[it[0]] || ""])),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const fixedTotal = cfg.wageItems.reduce((s, it) => s + (Number(f[it[0]]) || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/hrms/cn-payroll/employees/${row.employeeId}/profile`, f);
      toast.success(T("薪资档案已保存", "Profile saved")); onSaved(data);
    } catch (e) { toast.error(errMsg(e, T("保存失败", "Save failed"))); }
    setBusy(false);
  };
  const lab = "block text-xs text-slate-500";
  const num = (k, tid) => <input data-testid={tid} type="number" step="0.01" className={inp} value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder="0" />;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{T("¥ 薪资档案", "CNY Salary Profile")} · {row.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm" data-testid="hrms-cn-prof-dialog">
          <div>
            <p className="text-xs font-bold text-slate-600 mb-1.5">{T("一、月固定工资结构", "1. Fixed monthly wage structure")}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              {cfg.wageItems.map((it) => <label key={it[0]} className={lab}>{L(it, zh)}{num(it[0], `hrms-cn-pf-${it[0]}`)}</label>)}
            </div>
            <p className="text-xs text-slate-500 mt-2">{T("固定工资合计", "Fixed total")} <b className="text-slate-800" data-testid="hrms-cn-pf-total">{fmtY(fixedTotal)}</b></p>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-600 mb-1.5">{T("二、五险一金", "2. Social insurance & housing fund")}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <label className={lab}>{T("社保基数（空=固定工资）", "SI base (blank = fixed)")}{num("siBase", "hrms-cn-pf-siBase")}</label>
              <label className={lab}>{T("公积金基数（空=固定工资）", "HF base (blank = fixed)")}{num("hfBase", "hrms-cn-pf-hfBase")}</label>
              <label className={lab}>{T(`公积金单位 %（空=${cfg.rates?.housingFund?.er}）`, `HF employer % (blank=${cfg.rates?.housingFund?.er})`)}{num("hfRateEr", "hrms-cn-pf-hfRateEr")}</label>
              <label className={lab}>{T(`公积金个人 %（空=${cfg.rates?.housingFund?.ee}）`, `HF employee % (blank=${cfg.rates?.housingFund?.ee})`)}{num("hfRateEe", "hrms-cn-pf-hfRateEe")}</label>
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-600">
              <label className="inline-flex items-center gap-1.5"><input data-testid="hrms-cn-pf-siEnrolled" type="checkbox" checked={f.siEnrolled} onChange={(e) => set("siEnrolled", e.target.checked)} />{T("参加社保", "Enrolled in SI")}</label>
              <label className="inline-flex items-center gap-1.5"><input data-testid="hrms-cn-pf-hfEnrolled" type="checkbox" checked={f.hfEnrolled} onChange={(e) => set("hfEnrolled", e.target.checked)} />{T("缴存公积金", "Enrolled in HF")}</label>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-600 mb-1.5">{T("三、账户信息", "3. Accounts")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
              {TEXT_FIELDS.map((it) => <label key={it[0]} className={lab}>{L(it, zh)}<input data-testid={`hrms-cn-pf-${it[0]}`} className={inp} value={f[it[0]]} onChange={(e) => set(it[0], e.target.value)} placeholder={it[0] === "siCity" ? cfg.city : ""} /></label>)}
            </div>
          </div>
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("取消", "Cancel")}</button>
            <button data-testid="hrms-cn-pf-save" disabled={busy} onClick={save} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存档案", "Save profile")}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
